import { InfiniteData, QueryKey, useInfiniteQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo } from 'react'
import { OpalApiRequestParams, OpalApiResponseDocument } from '../schema'

type PaginatedOperations = 'ReadUsersV2' | 'ReadViewsV3' | 'ReadMomentsV3'

type SuccessfulCollectionResponse<TOperationId extends PaginatedOperations> =
  OpalApiResponseDocument<TOperationId, 200>

type UsePaginatedQueryArgs<
  TOperationId extends PaginatedOperations,
  TSelectedData,
> = {
  enabled?: boolean
  /** Defaults to 150 */
  limit?: number
  /**
   * resource queryKey, omitting any request params, which
   * will be supplied here to include page information
   */
  queryKeyWithoutParams: QueryKey
  /**
   * The `readCollection` function that makes the API request.
   * E.g. `requestFn: moments.readCollection`
   */
  requestFn: (
    params: OpalApiRequestParams<TOperationId>
  ) => Promise<SuccessfulCollectionResponse<TOperationId>>
  requestParams: OpalApiRequestParams<TOperationId>
  select: (
    data?: InfiniteData<SuccessfulCollectionResponse<TOperationId>>
  ) => TSelectedData
}

export type UsePaginatedQueryValue<TSelectedData> = {
  fetchingComplete: boolean
  data: TSelectedData
}

export const usePaginatedQuery = <
  TOperationId extends PaginatedOperations,
  TSelectedData,
>({
  enabled,
  limit = 150,
  queryKeyWithoutParams,
  requestFn,
  requestParams,
  select,
}: UsePaginatedQueryArgs<
  TOperationId,
  TSelectedData
>): UsePaginatedQueryValue<TSelectedData> => {
  const defaultPage = useMemo(() => ({ offset: 0, limit }), [limit])

  const requestParamsWithPageOffset = useCallback(
    (page?: { offset: number; limit: number }) => ({
      ...requestParams,
      query: {
        ...requestParams.query,
        page,
      },
    }),
    [requestParams]
  )

  const { data, isFetchingNextPage, isFetching, fetchNextPage, hasNextPage } =
    useInfiniteQuery({
      enabled,
      refetchInterval: false,
      queryKey: [
        ...queryKeyWithoutParams,
        requestParamsWithPageOffset(defaultPage),
      ],
      queryFn: ({ pageParam }) =>
        requestFn(requestParamsWithPageOffset(pageParam ?? defaultPage)),
      getNextPageParam: (lastPage, pages) => {
        const nextOffset = pages.length * limit
        if (!lastPage.meta) return defaultPage
        // If we've fetched the total resources return undefined so
        // hasNextPage will be false
        if (lastPage.meta.total < nextOffset) return undefined

        return { offset: nextOffset, limit }
      },
    })

  const resultsObject = useMemo(() => {
    const selectedData = select(data)
    return {
      fetchingComplete: !hasNextPage && !isFetching,
      data: selectedData,
    }
  }, [data, hasNextPage, isFetching, select])

  useEffect(() => {
    if (!data) return
    if (hasNextPage && !isFetching && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, isFetching, data, fetchNextPage])

  return resultsObject
}
